import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  createTheme,
  Divider,
  Indicator,
  Input,
  Modal,
  NumberInput,
  Paper,
  PasswordInput,
  Select,
  Table,
  Textarea,
  TextInput,
  Tree,
  type CSSVariablesResolver,
} from '@mantine/core'
import inputClasses from './input.module.css'

const green = [
  'hsl(78, 72%, 96%)', // 0
  'hsl(80, 66%, 91%)', // 1
  'hsl(82, 60%, 82%)', // 2
  'hsl(84, 56%, 69%)', // 3
  'hsl(86, 54%, 55%)', // 4
  'hsl(87, 58%, 39%)', // 5
  'hsl(90, 62%, 34%)', // 6
  'hsl(94, 66%, 26%)', // 7
  'hsl(100, 70%, 18%)', // 8
  'hsl(108, 74%, 11%)', // 9
] as const

const gray = [
  'hsl(100, 20%, 97%)', // 0
  'hsl(98, 18%, 94%)', // 1
  'hsl(96, 15%, 88%)', // 2
  'hsl(95, 12%, 72%)', // 3
  'hsl(94, 10%, 54%)', // 4
  'hsl(92, 10%, 40%)', // 5
  'hsl(105, 12%, 24%)', // 6
  'hsl(108, 14%, 18%)', // 7
  'hsl(112, 16%, 13%)', // 8
  'hsl(116, 18%, 9%)', // 9
] as const

const red = [
  '#FBEDEA', // 0
  '#F2D5CF', // 1
  '#E8BDB4', // 2
  '#DEA599', // 3
  '#D48D7E', // 4
  '#C4452E', // 5
  '#8A2F1E', // 6
  '#6E2518', // 7
  '#521B12', // 8
  '#36110C', // 9
] as const

const blue = [
  '#EAF2FB', // 0
  '#CFE0F2', // 1
  '#A8C8E8', // 2
  '#7FAEDD', // 3
  '#5694D2', // 4
  '#2D6CB5', // 5
  '#245A96', // 6
  '#1B4877', // 7
  '#123658', // 8
  '#0A2439', // 9
] as const

const amber = [
  '#FDF8E8', // 0
  '#F0E3BE', // 1
  '#E4CF94', // 2
  '#D8BB6A', // 3
  '#CCA740', // 4
  '#C08A12', // 5
  '#8A6209', // 6
  '#6E4E07', // 7
  '#523A05', // 8
  '#362603', // 9
] as const

export const consoleCssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    '--edy-green': 'var(--mantine-color-green-5)',
    '--edy-green-strong': 'var(--mantine-color-green-7)',
    '--edy-green-soft': 'var(--mantine-color-green-0)',
    '--edy-green-line': 'var(--mantine-color-green-1)',
    '--edy-blue': 'var(--mantine-color-blue-5)',
    '--edy-blue-soft': 'var(--mantine-color-blue-0)',
    '--edy-blue-line': 'var(--mantine-color-blue-1)',
    '--edy-amber': 'var(--mantine-color-amber-5)',
    '--edy-amber-ink': 'var(--mantine-color-amber-6)',
    '--edy-amber-soft': 'var(--mantine-color-amber-0)',
    '--edy-amber-line': 'var(--mantine-color-amber-1)',
    '--edy-danger': 'var(--mantine-color-red-5)',
    '--edy-danger-soft': 'var(--mantine-color-red-0)',
    '--edy-danger-line': 'var(--mantine-color-red-1)',
    '--edy-ink': 'var(--mantine-color-gray-6)',
    '--edy-ink-muted': 'var(--mantine-color-gray-5)',
    '--edy-background': 'var(--mantine-color-gray-0)',
    '--edy-surface': 'var(--mantine-color-white)',
    '--edy-line': 'var(--mantine-color-gray-2)',
    '--edy-sidebar-width': '272px',
    '--edy-sidebar-rail-width': '68px',
    '--edy-header-height': '60px',
  },
  light: {
    '--mantine-color-dimmed': 'var(--mantine-color-gray-5)',
    '--mantine-color-default-border': 'var(--mantine-color-gray-2)',
  },
  dark: {},
})

export const consoleTheme = createTheme({
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSizes: {
    xs: '12px',
    sm: '13px',
    md: '14px',
    lg: '15px',
    xl: '16px',
  },
  headings: {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontWeight: '700',
    sizes: {
      h1: { fontSize: '22px', lineHeight: '1.25' },
      h2: { fontSize: '18px', lineHeight: '1.3' },
      h3: { fontSize: '15px', lineHeight: '1.35' },
    },
  },
  primaryColor: 'green',
  primaryShade: 5,
  autoContrast: true,
  luminanceThreshold: 0.3,
  defaultRadius: 10,
  colors: {
    green: [...green],
    gray: [...gray],
    red: [...red],
    blue: [...blue],
    amber: [...amber],
  },
  black: 'var(--mantine-color-gray-8)',
  components: {
    Modal: Modal.extend({
      defaultProps: {
        centered: true,
        radius: 18,
        size: 700,
      },
      styles: {
        header: {
          padding: '18px 22px',
          borderBottom: '1px solid var(--edy-line)',
        },
      },
    }),

    Alert: Alert.extend({
      defaultProps: {
        radius: 12,
      },
      styles: (_theme, props) => {
        const tones = {
          blue: {
            background: 'var(--edy-blue-soft)',
            borderColor: 'var(--edy-blue-line)',
            color: '#24405e',
            icon: 'var(--edy-blue)',
          },
          amber: {
            background: 'var(--edy-amber-soft)',
            borderColor: 'var(--edy-amber-line)',
            color: 'var(--edy-amber-ink)',
            icon: 'var(--edy-amber)',
          },
          green: {
            background: 'var(--edy-green-soft)',
            borderColor: 'var(--edy-green-line)',
            color: 'var(--edy-green-strong)',
            icon: 'var(--edy-green)',
          },
          red: {
            background: 'var(--edy-danger-soft)',
            borderColor: 'var(--edy-danger-line)',
            color: '#8a2f1e',
            icon: 'var(--edy-danger)',
          },
        } as const
        const tone = tones[(props.color ?? 'blue') as keyof typeof tones]

        return {
          root: {
            border: '1px solid',
            background: tone?.background,
            borderColor: tone?.borderColor,
            color: tone?.color,
          },
          title: { color: 'inherit' },
          icon: { marginTop: 2, color: tone?.icon },
        }
      },
    }),

    Divider: Divider.extend({
      styles: {
        root: {
          '--divider-color': 'var(--mantine-color-gray-2)',
        },
      },
    }),

    Indicator: Indicator.extend({
      defaultProps: {
        fw: 700,
      },
      styles: {
        indicator: {
          fontSize: '9px',
        },
      },
    }),

    Button: Button.extend({
      defaultProps: { radius: 10 },
    }),

    Tree: Tree.extend({
      defaultProps: {
        levelOffset: 'md',
        selectOnClick: true,
        clearSelectionOnOutsideClick: false,
      },
      styles: {
        root: {
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        },
        node: {
          borderRadius: 10,
        },
        subtree: {
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          marginTop: 4,
          paddingInlineStart: 'calc(1.6rem * var(--mantine-scale))',
        },
        label: {
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          borderRadius: 10,
          padding: '8px 10px',
          color: 'var(--edy-ink)',
          '&:hover': {
            background: 'var(--edy-background)',
          },
          '&[data-selected]': {
            background: 'var(--edy-green-soft)',
            color: 'var(--edy-green-strong)',
          },
          '&:focus-visible': {
            outline: '2px solid var(--edy-green)',
            outlineOffset: 2,
          },
        },
      },
    }),

    Badge: Badge.extend({
      defaultProps: { radius: 'xl', variant: 'light' },
      styles: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
    }),

    Input: Input.extend({
      classNames: {
        input: inputClasses.input,
      },
    }),

    TextInput: TextInput.extend({
      defaultProps: { radius: 10, size: 'sm' },
      classNames: {
        input: inputClasses.input,
      },
    }),

    Textarea: Textarea.extend({
      defaultProps: { radius: 10, size: 'sm' },
      classNames: {
        input: inputClasses.input,
      },
    }),

    NumberInput: NumberInput.extend({
      defaultProps: { radius: 10, size: 'sm' },
      classNames: {
        input: inputClasses.input,
      },
    }),

    PasswordInput: PasswordInput.extend({
      defaultProps: { radius: 10, size: 'sm' },
      classNames: {
        input: inputClasses.input,
      },
    }),

    Select: Select.extend({
      defaultProps: { radius: 10, size: 'sm', comboboxProps: { withinPortal: true } },
      classNames: {
        input: inputClasses.input,
      },
    }),

    Checkbox: Checkbox.extend({
      styles: {
        input: {
          borderColor: '#d4d9ce',
        },
      },
    }),

    Paper: Paper.extend({
      defaultProps: { radius: 14, withBorder: true },
      styles: {
        root: {
          '--paper-border-color': 'var(--mantine-color-gray-2)',
        },
      },
    }),

    ActionIcon: ActionIcon.extend({
      defaultProps: { radius: 10, variant: 'subtle' },
    }),

    Table: Table.extend({
      defaultProps: {
        verticalSpacing: 'sm',
        horizontalSpacing: 'md',
        highlightOnHover: true,
      },
      styles: {
        table: {
          '--table-border-color': 'var(--mantine-color-gray-2)',
        },
      },
    }),
  },
})
